import { Edit3, PowerOff } from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';

interface RecipeCardData {
  id: number;
  active: boolean;
  name: string;
  description?: string | null;
  menuItemId: number;
  menuItemCode?: string | null;
  menuItemName?: string | null;
  yieldQuantity: number | null;
  yieldUnitCode?: string | null;
  ingredientCount: number;
}

interface RecipeCardProps {
  recipe: RecipeCardData;
  onEdit: () => void;
  onDeactivate: () => void;
}

const formatQuantity = (
  value: number | null,
  unitCode?: string | null
) =>
  `${Number(value ?? 0).toLocaleString('es-SV', {
    maximumFractionDigits: 6,
  })} ${unitCode || ''}`.trim();

const RecipeCard = ({
  recipe,
  onEdit,
  onDeactivate,
}: RecipeCardProps) => (
  <article
    className={`flex h-full flex-col rounded-xl border p-5 shadow-card-2 transition-shadow duration-200 hover:shadow-card dark:shadow-none ${
      recipe.active
        ? 'border-stroke bg-white dark:border-strokedark dark:bg-boxdark'
        : 'border-stroke bg-gray-2/60 dark:border-strokedark dark:bg-boxdark-2/60'
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p
          className="truncate text-xs font-semibold uppercase tracking-wider text-primary"
          title={`${recipe.menuItemCode || 'Sin código'} · ${
            recipe.menuItemName || 'Sin producto'
          }`}
        >
          {recipe.menuItemCode || 'Sin código'} ·{' '}
          {recipe.menuItemName || 'Sin producto'}
        </p>

        <h3
          className="mt-1 truncate font-semibold text-black dark:text-white"
          title={recipe.name}
        >
          {recipe.name}
        </h3>
      </div>

      <span
        className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
          recipe.active
            ? 'bg-success/10 text-success'
            : 'bg-danger/10 text-danger'
        }`}
      >
        {recipe.active ? 'Activa' : 'Inactiva'}
      </span>
    </div>

    <p className="mt-3 line-clamp-2 min-h-[40px] text-sm text-bodydark2">
      {recipe.description || 'Sin descripción'}
    </p>

    <div className="mt-5 grid grid-cols-2 gap-3">
      <div className="rounded-lg border border-stroke/70 bg-gray-2 p-3 dark:border-strokedark dark:bg-meta-4">
        <p className="text-xs text-bodydark2">
          Rendimiento
        </p>

        <p className="mt-1 font-semibold text-black dark:text-white">
          {formatQuantity(
            recipe.yieldQuantity,
            recipe.yieldUnitCode
          )}
        </p>
      </div>

      <div className="rounded-lg border border-stroke/70 bg-gray-2 p-3 dark:border-strokedark dark:bg-meta-4">
        <p className="text-xs text-bodydark2">
          Ingredientes
        </p>

        <p className="mt-1 font-semibold text-black dark:text-white">
          {recipe.ingredientCount}
        </p>
      </div>
    </div>

    <div
      className={`mt-auto grid gap-2 border-t border-stroke pt-4 dark:border-strokedark ${
        recipe.active ? 'grid-cols-2' : 'grid-cols-1'
      }`}
    >
      <ActionButton
        label="Editar"
        icon={<Edit3 className="h-4 w-4" />}
        variant="primary"
        size="sm"
        onClick={onEdit}
        className="min-h-[38px]"
      />

      {recipe.active && (
        <ActionButton
          label="Desactivar"
          icon={<PowerOff className="h-4 w-4" />}
          variant="danger"
          size="sm"
          onClick={onDeactivate}
          className="min-h-[38px]"
        />
      )}
    </div>
  </article>
);

export default RecipeCard;